import BaseSync from "./base";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { getServerDate } from "../utils/storage/date";
import * as logger from "../utils/logger";
import Trip from "../model/Trip";
import { handleApi } from "../utils/error";
import { NODEVICEID, NOUSERID } from "../model/DutyStatusLog";
import { getTripStartOfDay, TripActions } from "../store/trips/actions";
import { hashToArray } from "../utils/vanilla";
import { getLogRecords, ILogRecordLookup, TSyncCallback, preloadVehicles } from "./common";
import { getStartOfDayFrom } from "../utils/trip/tripHelper";
import { ISyncInterval } from "../store/mobile/types";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";

export default class TripSync extends BaseSync {
    defaults() {
        return {
            name: "Sync: Trips"
        };
    }

    synchronize(progressCb: TSyncCallback, profilingBatcher?: PerformanceProfileBatcher): Promise<ISyncInterval> {
        return this.createProfiledSync(MeasureName.SyncTrip, () => {
            // NOTE: The sync interval for a run is computed once here and threaded through as a
            // local value (fetchData -> syncTrips/mergeTrips -> resolve) rather than stored on a
            // shared instance field. Otherwise a concurrent synchronize() call could reassign the
            // shared field before this run's async continuation reads it back, causing this run to
            // filter/merge against the wrong date window. See MYG-143684.
            const syncInterval = this.getTripSyncInterval();
            // Same rationale as syncInterval above: capture the user for this run as a local value
            // instead of relying on the shared `this.currentUser` field, which is also reassigned by
            // fetchData(). See MYG-143684.
            const currentUser = this.api.getUser();

            // Last sync might have failed due to rate limiting. Don't repeat those calls
            if (this.didSyncFailRecently()) {
                logger.event(logger.Event.SYNCTRIPCONFLICT, "Skip Trip sync because previous run failed very recently");
                progressCb("Trip");
                return Promise.resolve(syncInterval);
            }

            return new Promise(resolve => {
                let handleSyncSuccess = () => this.updateRunStateAtSyncComplete();
                let handleSyncError = error => this.updateRunStateAtSyncComplete(error);

                this.fetchData(syncInterval, currentUser)
                    .then(serverTrips => this.syncTrips(serverTrips, syncInterval))
                    .then(mergedTrips => this.fetchLogRecordsIfRequired(mergedTrips))
                    .then(mergedTrips => this.preloadVehicles(mergedTrips, currentUser))
                    .then(() => {
                        progressCb("Trip");
                        handleSyncSuccess();
                        resolve(syncInterval);
                    })
                    .catch(err => {
                        progressCb("Trip", err);
                        resolve(syncInterval);
                        handleSyncError(err);
                    });
            });
        }, profilingBatcher);
    }

    getTripSyncInterval(): ISyncInterval {
        let syncInterval = this.store.getState().mobile.tripCategorizationSyncInterval;
        let fromDate = syncInterval?.fromDate || getStartOfDayFrom(2).toISOString();
        let toDate = syncInterval?.toDate || getServerDate().toISOString();
        return { fromDate, toDate };
    }

    populateLocationWithLogRecords (mergedTrips: IIdDictionary<Trip>, tripsWithMissingStartPoint: Trip[]): Promise<IIdDictionary<Trip>> {
        return new Promise((resolve, reject) => {
            let logRecordLookups: ILogRecordLookup[] = [];

            tripsWithMissingStartPoint.forEach(trip => {
                if (trip.start) {
                    logRecordLookups.push({
                        id: trip.id,
                        dateTime: trip.start,
                        deviceId: trip.device.id
                    });
                }
            });

            getLogRecords(logRecordLookups, this.api)
                .then(logRecords => {
                    logRecords.forEach((record, i) => {
                        let location = {
                            x: record.length > 0 ? record[0].longitude : 0,
                            y: record.length > 0 ? record[0].latitude : 0
                        };

                        let tripId = logRecordLookups[i].id;

                        let trip = mergedTrips[tripId];
                        if (trip) {
                            trip._startPoint = location;
                        }
                    });
                    resolve(mergedTrips);
                }).catch(reject);
        });
    }

     getTripsWithMissingStartPoints(mergedTrips: IIdDictionary<Trip>, tripsWithMissingStartPoint: Trip[]) {
        let userTrips = hashToArray(mergedTrips);
        if (userTrips.length === 0) {
            return;
        }
        let sortedUserTrips = userTrips.sort((a, b) => (!a.start || !b.start) ? 0 : (a.start > b.start ? 1 : -1));
        let firstTrip = sortedUserTrips[0];
        if (!firstTrip._startPoint) {
            tripsWithMissingStartPoint.push(firstTrip);
        }

        for (let i = 1; i < sortedUserTrips.length; i++) {
            let currentTrip = sortedUserTrips[i];
            let previousTrip = sortedUserTrips[i - 1];
            if ((previousTrip.nextTripStart !== currentTrip.start || previousTrip.device.id !== currentTrip.device.id)
                && !currentTrip._startPoint) {
                tripsWithMissingStartPoint.push(currentTrip);
            } else {
                mergedTrips[currentTrip.id]._startPoint = previousTrip.stopPoint;
            }
        }
    }

    preloadVehicles(mergedTrips: IIdDictionary<Trip>, currentUser: IUser | null) {
        this.setTrips(mergedTrips, currentUser);
        let activeVehicles = {};
        Object.keys(mergedTrips).forEach(tripId => {
            let deviceId = mergedTrips[tripId].device.id;
            if (deviceId !== NODEVICEID && !activeVehicles.hasOwnProperty(deviceId)) {
                activeVehicles[deviceId] = { id: deviceId };
            }
        });
        return preloadVehicles(activeVehicles, currentUser, this.api, this.store);
    }

    extendSyncIntervalBasedOnTrips(trips: Trip[], syncInterval: ISyncInterval) {
        let sortedTrips = trips.sort((a, b) => (!a.start || !b.start) ? 0 : (a.start > b.start ? 1 : -1));
        if (syncInterval.fromDate && sortedTrips[0]?.start && sortedTrips[0].start < syncInterval.fromDate) {
            syncInterval.extendedFromDate = sortedTrips[0].start;
        }
    }

    fetchData(syncInterval: ISyncInterval, currentUser: IUser | null): Promise<Trip[]> {
        // Keep this.currentUser updated for other BaseSync consumers (e.g. error logging), but this
        // call itself must use the `currentUser` argument, not the shared field, so a concurrent
        // synchronize() call reassigning this.currentUser can't affect this run. See MYG-143684.
        this.currentUser = currentUser;
        return new Promise((resolve, reject) => {
            if (currentUser !== null && currentUser.id !== NOUSERID) {

                let queryParams = {
                    typeName: "Trip",
                    search: {
                        userSearch: {id: currentUser.id},
                        fromDate: syncInterval.fromDate,
                        toDate: syncInterval.toDate,
                        includeOverlappedTrips: true
                    }
                };

                this.api.call(
                    "Get",
                    queryParams,
                    trips => {
                        let tripEntities = this.convertTripsFromServer(trips);
                        logger.log(logger.Source.SYNC, `Downloaded ${ trips.length } trips`, currentUser.id);
                        this.extendSyncIntervalBasedOnTrips(tripEntities, syncInterval);
                        resolve(tripEntities);
                    },
                    (err) => {
                        handleApi(err);
                        reject(err);
                    }
                );
            } else {
                resolve([]);
            }
        });
    }

    getTrips(overrideUser?: string): IIdDictionary<Trip> {
        const userId = overrideUser ?? this.userService.getCurrentUser()?.id;
        if (!userId) {
            throw new Error("Invalid UserId");
        }
        let trips = this.store.getState().trips[userId];
        return this.convertToDictionary(trips);
    }

    mergeTrips(serverTrips: Trip[], localTrips: IIdDictionary<Trip>, syncInterval: ISyncInterval): IIdDictionary<Trip> {
        // Remove trips within the same interval and overwrite with server's trips
        let localTripsArray = hashToArray(localTrips);
        let fromDate = new Date(syncInterval.fromDate as string).getTime();
        let toDate = new Date(syncInterval.toDate as string).getTime();

        // Filter all trips that are NOT in the interval, and combine with server trips
        let updatedTrips = localTripsArray
                            .filter((trip: Trip) => (trip.start && new Date(trip.start).getTime() > toDate) || (trip.nextTripStart && new Date(trip.nextTripStart).getTime() < fromDate));

        let allTrips = serverTrips.concat(updatedTrips);
        let newLocalTrips = {};
        allTrips.forEach((trip: Trip) => {
            trip._midPoint = this.getMidpoint(trip.start, trip.stop);
            newLocalTrips[trip.id] = trip;
        });
        return newLocalTrips;
    }

    convertTripsFromServer(trips: any[]): Trip[] {
        let newTrips: Trip[] = [];
        if (trips) {
            for (let trip of trips) {
                newTrips.push(new Trip(trip));
            }
        }
        return newTrips;
    }

    parseDate(dateString: string): Date {
        return new Date(dateString);
    }

    getMidpoint(start: string | undefined, stop: string | undefined): string | undefined {
        if (!start || !stop) {
            return undefined; // If either start or stop is undefined, return undefined as midpoint cannot be calculated
        }

        const startDate = this.parseDate(start);
        const stopDate = this.parseDate(stop);

        const midTimestamp = (startDate.getTime() + stopDate.getTime()) / 2;
        return new Date(midTimestamp).toISOString();
    }

    private fetchLogRecordsIfRequired(mergedTrips: IIdDictionary<Trip>): Promise<IIdDictionary<Trip>> {
        return new Promise(resolve => {
            let tripsWithMissingStartPoint: Trip[] = [];
            this.getTripsWithMissingStartPoints(mergedTrips, tripsWithMissingStartPoint);
            return this.populateLocationWithLogRecords(mergedTrips, tripsWithMissingStartPoint).then(resolve);
        });
    }

    private setTrips(tripsToSave: IIdDictionary<Trip>, currentUser: IUser | null) {
        let userId = currentUser ? currentUser.id : undefined;

        if (!userId){
            return;
        }
        this.store.dispatch(TripActions.setTrips({ userId, trips: tripsToSave, startOfDay: getTripStartOfDay(currentUser?.timeZoneId) }));
        return tripsToSave;
    }

    private convertToDictionary(trips: DeepReadonlyObject<IIdDictionary<Trip>>) {
        let tripsDictionary: IIdDictionary<Trip> = {};
        if (trips) {
            Object.keys(trips).forEach(key => {
                if (trips) {
                    tripsDictionary[key] = new Trip(trips[key]);
                }
            });
        }
        return tripsDictionary;
    }

    private syncTrips(serverTrips: Trip[], syncInterval: ISyncInterval): IIdDictionary<Trip> {
        let newTrips: IIdDictionary<Trip> = this.getTrips() || {};
        return this.mergeTrips(serverTrips || [], newTrips, syncInterval);
    }
}